'use client';

import { useState } from 'react';

import { getBillingAccessState } from '@/features/subscription/utils/billingAccess';
import { useGetCurrentUserContextQuery } from '@/features/users/api/usersApi';
import { useAppSelector } from '@/store/hooks';
import { ChatButton } from '@web/ui';

import { ChatContextDrawer } from './ChatContextDrawer';

import type { ChatContextTarget } from './ChatContextDrawer';
import type { SubscriptionCurrentPlan } from '@/features/subscription/types';

export function ChatContextButton({
  target,
  messageCount,
  unreadCount,
  mentionCount,
  disabled = false,
  disabledLabel,
}: {
  target: ChatContextTarget;
  messageCount?: number;
  unreadCount?: number;
  mentionCount?: number;
  disabled?: boolean;
  disabledLabel?: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const currentWorkspacePlan = useAppSelector(
    (state) =>
      (
        state.workspaces.currentWorkspace as {
          plan?: SubscriptionCurrentPlan | null;
        } | null
      )?.plan,
  );

  const {
    data: currentUserContextResponse,
    isLoading: isBillingAccessLoading,
  } = useGetCurrentUserContextQuery();
  const plan =
    currentUserContextResponse?.data?.workspace?.plan ?? currentWorkspacePlan;
  const billingAccessState = getBillingAccessState(plan);
  const hasPremiumAccess =
    billingAccessState === 'premium_active' ||
    billingAccessState === 'trial_active';
  const isDisabled =
    disabled || (!plan && isBillingAccessLoading) || !hasPremiumAccess;
  const resolvedDisabledLabel = disabled
    ? disabledLabel
    : !hasPremiumAccess
      ? 'Chat unavailable'
      : undefined;

  return (
    <>
      <ChatButton
        messageCount={messageCount}
        unreadCount={unreadCount}
        mentionCount={mentionCount}
        disabled={isDisabled}
        disabledLabel={resolvedDisabledLabel}
        onClick={() => {
          if (isDisabled) {
            return;
          }

          setIsMounted(true);
          setIsOpen(true);
        }}
      />
      {isMounted ? (
        <ChatContextDrawer
          open={isOpen}
          target={target}
          onClose={() => setIsOpen(false)}
          onExited={() => setIsMounted(false)}
        />
      ) : null}
    </>
  );
}
